import { useSyncExternalStore } from 'react';

/**
 * Is someone actually looking at this tab? Visible AND touched (pointer, key, wheel,
 * touch) within `IDLE_MS`. Anything that makes the server hit the DB on a timer — the
 * operations SSE stream, `refetchInterval` polling — must pause while this is false,
 * or a tab left open overnight keeps the scale-to-zero (Neon) DB awake 24/7.
 */
export const IDLE_MS = 30 * 60_000;
const CHECK_MS = 60_000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

let lastActivity = Date.now();
let active = true;
const listeners = new Set<() => void>();
let checkTimer: ReturnType<typeof setInterval> | undefined;

export function isPageActive(): boolean {
  return document.visibilityState === 'visible' && Date.now() - lastActivity < IDLE_MS;
}

function update(): void {
  const next = isPageActive();
  if (next === active) return;
  active = next;
  for (const listener of listeners) listener();
}

function onActivity(): void {
  lastActivity = Date.now();
  update();
}

if (typeof window !== 'undefined') {
  for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, onActivity, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onActivity();
    else update();
  });
}

/** Called whenever the active state flips. The idle check only runs while subscribed. */
export function subscribePageActivity(listener: () => void): () => void {
  listeners.add(listener);
  active = isPageActive();
  checkTimer ??= setInterval(update, CHECK_MS);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && checkTimer) {
      clearInterval(checkTimer);
      checkTimer = undefined;
    }
  };
}

export function usePageActive(): boolean {
  return useSyncExternalStore(subscribePageActivity, () => active);
}
